import React, { FunctionComponent, useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import clsx from 'clsx';
import { useRouter } from 'next/router';

import { ProductToggleFavoriteData } from '@domains/product';
import { RecipeToggleFavoriteData } from '@domains/recipes';

import { setIsOpenLogin } from '@stores/dialogs';
import { setLoginAction } from '@stores/login-action';
import { $isLoggedIn } from '@stores/profile';

import {
    apiFavoriteProductsAdd,
    apiFavoriteProductsRemove,
    apiFavoriteRecipesAdd,
    apiFavoriteRecipesRemove,
} from '@api/methods';

import {
    sendAddProductToFavoriteEvent,
    sendAddRecipeToFavoriteEvent,
    sendRemoveProductFromFavoriteEvent,
    sendRemoveRecipeFromFavoriteEvent,
} from '@utils/analytics';
import { SnackBar } from '@utils/snackbar';
import { applyTypograf } from '@utils/typograf';
import { upperCaseFirstCart } from '@utils/utils';

import { FavoriteIcon } from '@components/icons';

import styles from './FavoriteButton.module.scss';

type Size = 24 | 34 | 40 | 62;
type Type = 'product' | 'recipe';

const sizeClassMap: Record<Size, string> = {
    24: styles.isSmallSize,
    34: styles.isMediumSize,
    40: styles.isLargeSize,
    62: styles.isXLargeSize,
};

const typeTextMap: Record<Type, string> = {
    product: 'товар',
    recipe: 'рецепт',
};

type FavoriteButtonProps = {
    type: Type;
    id: number;
    initialIsFavorite: boolean;
    size?: Size;
    hasShadow?: boolean;
    isDisabled?: boolean;
    shouldReloadAfterLogin?: boolean;
    className?: string;
};
export const FavoriteButton: FunctionComponent<FavoriteButtonProps> = (props) => {
    const {
        type,
        id,
        initialIsFavorite,
        className,
        size = 34,
        hasShadow = false,
        isDisabled = false,
        shouldReloadAfterLogin = true,
    } = props;

    const router = useRouter();

    const [isLoggedIn, setIsOpenLoginFn, setLoginActionFn] = useUnit([
        $isLoggedIn,
        setIsOpenLogin,
        setLoginAction,
    ]);

    const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
    const [isLoading, setIsLoading] = useState(false);

    const toggleFavorite = () => setIsFavorite((flag) => !flag);

    const handleToggleFavorite = async () => {
        setIsLoading(true);

        try {
            if (type === 'product') {
                const payload: ProductToggleFavoriteData = {
                    product_id: id,
                };

                if (isFavorite) {
                    await apiFavoriteProductsRemove(payload);
                    sendRemoveProductFromFavoriteEvent(id);
                } else {
                    await apiFavoriteProductsAdd(payload);
                    sendAddProductToFavoriteEvent(id);
                }
            } else if (type === 'recipe') {
                const payload: RecipeToggleFavoriteData = {
                    recipe_id: id,
                };

                if (isFavorite) {
                    await apiFavoriteRecipesRemove(payload);
                    sendRemoveRecipeFromFavoriteEvent(id);
                } else {
                    await apiFavoriteRecipesAdd(payload);
                    sendAddRecipeToFavoriteEvent(id);
                }
            }
            toggleFavorite();
        } catch (error) {
            SnackBar.handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddToFavorites = async () => {
        setIsLoading(true);

        try {
            if (type === 'product') {
                await apiFavoriteProductsAdd({
                    product_id: id,
                });
                sendAddProductToFavoriteEvent(id);
            } else if (type === 'recipe') {
                await apiFavoriteRecipesAdd({
                    recipe_id: id,
                });
                sendAddRecipeToFavoriteEvent(id);
            }

            setIsFavorite(true);

            SnackBar.success(`${upperCaseFirstCart(typeTextMap[type])} добавлен в избранное`);

            if (shouldReloadAfterLogin) {
                setTimeout(() => router.reload(), 300);
            }
        } catch (error) {
            SnackBar.handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const onClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();

        if (isLoading) return;

        if (!isLoggedIn) {
            setIsOpenLoginFn(true);
            setLoginActionFn(handleAddToFavorites);
            SnackBar.warning(
                applyTypograf(`Добавление ${typeTextMap[type]}a в избранное требует авторизации`),
            );
            return;
        }

        await handleToggleFavorite();
    };

    return (
        <button
            type="button"
            className={clsx(
                styles.root,
                sizeClassMap[size],
                hasShadow && styles.shadow,
                isLoading && styles.isLoading,
                isFavorite && styles.isActive,
                isDisabled && !isFavorite && styles.isDisabled,
                className,
            )}
            onClick={onClick}
            disabled={isLoading || (isDisabled && !isFavorite)}
            aria-label={
                isFavorite
                    ? `Убрать ${typeTextMap[type]} из избранного`
                    : `Добавить ${typeTextMap[type]} в избранное`
            }
        >
            <FavoriteIcon className={styles.icon} />
        </button>
    );
};
